import React, { useState, useEffect } from 'react';
import {
  X,
  User,
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
  AlertCircle,
  FileText,
  Image as ImageIcon,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  ArrowRight,
  Upload,
  ZoomIn,
  ZoomOut,
  RotateCw,
} from 'lucide-react';
import {
  type ChangeRequest,
  reviewChangeRequest,
  getAdminUserDocuments,
  type UserDocument,
  FIELD_LABELS,
  type ChangeableField,
} from '@/services/changeRequestService';

interface ChangeRequestReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  request: ChangeRequest | null;
  onReviewComplete: () => void;
}

const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  CIN_FRONT: 'CIN (Recto)',
  CIN_BACK: 'CIN (Verso)',
  PASSPORT: 'Passeport',
  DIPLOME_BAC_SCAN: 'Diplôme Bac (Scan)',
  RELEVES_NOTES_BAC: 'Relevés de notes Bac',
  DIPLOME_ACCES_SCAN: 'Diplôme d\'accès (Scan)',
  RELEVES_NOTES: 'Relevés de notes',
  CV: 'CV',
  LETTRE_MOTIVATION: 'Lettre de motivation',
  PHOTO: 'Photo d\'identité',
  // Staff documents
  DIPLOME_SCAN: 'Diplôme',
  ATTESTATION_RIB: 'Attestation RIB',
  CONTRAT_TRAVAIL: 'Contrat de travail',
  AUTORISATION_ENSEIGNER: 'Autorisation d\'enseigner',
  ENGAGEMENT_PROFESSEUR: 'Engagement professeur',
};

// Document change request fields
const DOCUMENT_CHANGE_FIELDS = [
  'documentCinFront',
  'documentCinBack',
  'documentPassport',
  'documentCv',
  'documentDiplome',
  'documentPhoto',
  'documentRib',
];

// Helper to check if a request is a document change request
const isDocumentChangeRequest = (fieldName: string, requestedValue: string): boolean => {
  return DOCUMENT_CHANGE_FIELDS.includes(fieldName) || requestedValue.startsWith('DOCUMENT_UPDATE:');
};

// Helper to extract document URL from requested value
const extractDocumentUrl = (requestedValue: string): string => {
  if (requestedValue.startsWith('DOCUMENT_UPDATE:')) {
    // Format: DOCUMENT_UPDATE:/uploads/...|REASON:...
    const urlPart = requestedValue.replace('DOCUMENT_UPDATE:', '').split('|')[0];
    return urlPart;
  }
  return requestedValue;
};

// Helper to extract reason from requested value (for document requests)
const extractDocumentReason = (requestedValue: string): string | null => {
  if (requestedValue.includes('|REASON:')) {
    return requestedValue.split('|REASON:')[1];
  }
  return null;
};

// Helper to get full URL for document
const getFullDocumentUrl = (url: string): string => {
  if (url.startsWith('/')) {
    return `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000'}${url}`;
  }
  return url;
};

// Helper to check if URL is an image
const isImageUrl = (url: string): boolean => {
  return /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(url);
};

// Helper to check if URL is a PDF
const isPdfUrl = (url: string): boolean => {
  return /\.pdf$/i.test(url);
};

// Fields that require document verification
const FIELDS_REQUIRING_DOCUMENTS: string[] = [
  'firstName',
  'lastName',
  'prenomArabe',
  'nomArabe',
  'studentCnie',
  'studentPassport',
  'studentDateOfBirth',
  'studentNationality',
  'studentGender',
];

export const ChangeRequestReviewModal: React.FC<ChangeRequestReviewModalProps> = ({
  isOpen,
  onClose,
  request,
  onReviewComplete,
}) => {
  const [documents, setDocuments] = useState<UserDocument[]>([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [docsError, setDocsError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rejectComment, setRejectComment] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [selectedDocIndex, setSelectedDocIndex] = useState(0);
  const [showDocumentViewer, setShowDocumentViewer] = useState(false);

  // Document preview state for document change requests
  const [docPreviewZoom, setDocPreviewZoom] = useState(100);
  const [docPreviewRotation, setDocPreviewRotation] = useState(0);
  const [showFullscreenPreview, setShowFullscreenPreview] = useState(false);

  // Load user documents when modal opens
  useEffect(() => {
    if (isOpen && request?.userId) {
      loadDocuments(request.userId);
    }
  }, [isOpen, request?.userId]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setDocuments([]);
      setLoadingDocs(false);
      setDocsError(null);
      setProcessing(false);
      setError(null);
      setRejectComment('');
      setShowRejectForm(false);
      setSelectedDocIndex(0);
      setShowDocumentViewer(false);
      setDocPreviewZoom(100);
      setDocPreviewRotation(0);
      setShowFullscreenPreview(false);
    }
  }, [isOpen]);

  const loadDocuments = async (userId: string) => {
    setLoadingDocs(true);
    setDocsError(null);
    try {
      const docs = await getAdminUserDocuments(userId);
      setDocuments(docs);
    } catch (err) {
      setDocsError(err instanceof Error ? err.message : 'Erreur lors du chargement des documents');
    } finally {
      setLoadingDocs(false);
    }
  };

  const handleApprove = async () => {
    if (!request) return;

    setProcessing(true);
    setError(null);
    try {
      await reviewChangeRequest(request.id, { decision: 'APPROVED' });
      onClose();
      onReviewComplete();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erreur lors de l\'approbation';
      // If the request was already processed, close the modal and refresh
      if (errorMessage.includes('déjà été traitée') || errorMessage.includes('already')) {
        onClose();
        onReviewComplete();
      } else {
        setError(errorMessage);
      }
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!request) return;

    if (!rejectComment.trim()) {
      setError('Un commentaire est requis pour refuser une demande');
      return;
    }

    setProcessing(true);
    setError(null);
    try {
      await reviewChangeRequest(request.id, {
        decision: 'REJECTED',
        comment: rejectComment,
      });
      onClose();
      onReviewComplete();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erreur lors du refus';
      // If the request was already processed, close the modal and refresh
      if (errorMessage.includes('déjà été traitée') || errorMessage.includes('already')) {
        onClose();
        onReviewComplete();
      } else {
        setError(errorMessage);
      }
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getFieldLabel = (fieldName: string): string => {
    return FIELD_LABELS[fieldName as ChangeableField] || fieldName;
  };

  const getRoleName = (role: string): string => {
    const roleNames: Record<string, string> = {
      STUDENT: 'Étudiant',
      PARENT: 'Parent',
      TEACHER: 'Professeur',
      DIRECTOR_GENERAL: 'Directeur Général',
      PEDAGOGICAL_DIRECTOR: 'Directeur Pédagogique',
      BRANCH_MANAGER: 'Responsable de Site',
      PROGRAM_HEAD: 'Chef de Filière',
      ACADEMIC_COORDINATOR: 'Coordinateur Académique',
      FINANCIAL_MANAGER: 'Responsable Financier',
      MARKETING_MANAGER: 'Responsable Marketing',
      COMMERCIAL_MANAGER: 'Responsable Commercial',
      SUPER_ADMIN: 'Super Administrateur',
    };
    return roleNames[role] || role;
  };

  const requiresDocumentVerification = (fieldName: string): boolean => {
    return FIELDS_REQUIRING_DOCUMENTS.includes(fieldName);
  };

  const getDocumentUrl = (doc: UserDocument): string => {
    // Handle relative URLs
    if (doc.fileUrl.startsWith('/')) {
      return `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000'}${doc.fileUrl}`;
    }
    return doc.fileUrl;
  };

  const isImageDocument = (doc: UserDocument): boolean => {
    return doc.mimeType?.startsWith('image/') || /\.(jpg|jpeg|png|gif|webp)$/i.test(doc.fileName);
  };

  const handlePrevDocument = () => {
    setSelectedDocIndex((prev) => (prev > 0 ? prev - 1 : documents.length - 1));
  };

  const handleNextDocument = () => {
    setSelectedDocIndex((prev) => (prev < documents.length - 1 ? prev + 1 : 0));
  };

  if (!isOpen || !request) return null;

  const showDocWarning = requiresDocumentVerification(request.fieldName) && documents.length === 0;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/50 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden z-10">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Clock className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Examiner la demande de modification
                </h2>
                <p className="text-sm text-gray-500">
                  Vérifiez les informations et les documents avant d'approuver ou refuser
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="overflow-y-auto max-h-[calc(90vh-180px)]">
            <div className="p-6 space-y-6">
              {/* Error Alert */}
              {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                  <button
                    onClick={() => setError(null)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}

              {/* User Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
                    <User className="w-6 h-6 text-primary-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">
                      {request.user?.firstName} {request.user?.lastName}
                    </p>
                    <div className="flex items-center gap-3 text-sm text-gray-500">
                      <span>{request.user?.email}</span>
                      <span className="px-2 py-0.5 bg-gray-200 text-gray-600 rounded text-xs">
                        {request.user?.role && getRoleName(request.user.role)}
                      </span>
                    </div>
                  </div>
                  <div className="text-right text-sm text-gray-500">
                    <p>Soumise le</p>
                    <p className="font-medium text-gray-700">{formatDate(request.createdAt)}</p>
                  </div>
                </div>
              </div>

              {/* Change Request Details */}
              <div className="bg-white border border-gray-200 rounded-lg p-5">
                <h3 className="font-medium text-gray-900 mb-4">Détails de la modification</h3>

                <div className="space-y-4">
                  <div>
                    <span className="text-sm font-medium text-gray-500">Champ à modifier</span>
                    <p className="mt-1 text-gray-900 font-medium">{getFieldLabel(request.fieldName)}</p>
                  </div>

                  {/* Document Change Request - Show document preview */}
                  {isDocumentChangeRequest(request.fieldName, request.requestedValue) ? (
                    <div className="space-y-4">
                      {/* Document type info */}
                      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-3">
                        <Upload className="w-5 h-5 text-blue-600" />
                        <div>
                          <p className="text-sm font-medium text-blue-800">
                            Demande de modification de document
                          </p>
                          <p className="text-xs text-blue-600">
                            Vérifiez le nouveau document ci-dessous avant d'approuver
                          </p>
                        </div>
                      </div>

                      {/* Current vs New Document */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {/* Current Document */}
                        <div className="bg-gray-50 rounded-lg p-4">
                          <span className="text-sm font-medium text-gray-500 mb-3 block">Document actuel</span>
                          {request.currentValue && request.currentValue !== '(Document actuel)' ? (
                            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                              {isImageUrl(request.currentValue) ? (
                                <img
                                  src={getFullDocumentUrl(request.currentValue)}
                                  alt="Document actuel"
                                  className="w-full h-48 object-contain bg-gray-100"
                                  onError={(e) => {
                                    (e.target as HTMLImageElement).style.display = 'none';
                                    (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden');
                                  }}
                                />
                              ) : isPdfUrl(request.currentValue) ? (
                                <iframe
                                  src={getFullDocumentUrl(request.currentValue)}
                                  title="Document actuel"
                                  className="w-full h-48"
                                />
                              ) : (
                                <div className="h-48 flex items-center justify-center text-gray-400">
                                  <FileText className="w-12 h-12" />
                                </div>
                              )}
                              <div className="p-2 border-t border-gray-200 flex justify-center">
                                <a
                                  href={getFullDocumentUrl(request.currentValue)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1"
                                >
                                  <ExternalLink className="w-3 h-3" />
                                  Ouvrir en grand
                                </a>
                              </div>
                            </div>
                          ) : (
                            <div className="h-48 bg-white rounded-lg border border-gray-200 flex items-center justify-center text-gray-400">
                              <div className="text-center">
                                <FileText className="w-10 h-10 mx-auto mb-2" />
                                <p className="text-sm">Aucun document actuel</p>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* New Document */}
                        <div className="bg-green-50 rounded-lg p-4">
                          <span className="text-sm font-medium text-gray-500 mb-3 block">Nouveau document</span>
                          {(() => {
                            const newDocUrl = extractDocumentUrl(request.requestedValue);
                            const fullUrl = getFullDocumentUrl(newDocUrl);
                            return (
                              <div className="bg-white rounded-lg border border-green-200 overflow-hidden">
                                {isImageUrl(newDocUrl) ? (
                                  <div className="relative">
                                    <img
                                      src={fullUrl}
                                      alt="Nouveau document"
                                      className="w-full h-48 object-contain bg-gray-100 cursor-pointer hover:opacity-90 transition-opacity"
                                      style={{
                                        transform: `scale(${docPreviewZoom / 100}) rotate(${docPreviewRotation}deg)`,
                                      }}
                                      onClick={() => setShowFullscreenPreview(true)}
                                    />
                                    {/* Zoom/Rotate controls */}
                                    <div className="absolute bottom-2 right-2 flex gap-1 bg-black/50 rounded-lg p-1">
                                      <button
                                        onClick={() => setDocPreviewZoom(z => Math.max(50, z - 25))}
                                        className="p-1 text-white hover:bg-white/20 rounded"
                                        title="Réduire"
                                      >
                                        <ZoomOut className="w-4 h-4" />
                                      </button>
                                      <button
                                        onClick={() => setDocPreviewZoom(z => Math.min(200, z + 25))}
                                        className="p-1 text-white hover:bg-white/20 rounded"
                                        title="Agrandir"
                                      >
                                        <ZoomIn className="w-4 h-4" />
                                      </button>
                                      <button
                                        onClick={() => setDocPreviewRotation(r => (r + 90) % 360)}
                                        className="p-1 text-white hover:bg-white/20 rounded"
                                        title="Pivoter"
                                      >
                                        <RotateCw className="w-4 h-4" />
                                      </button>
                                    </div>
                                  </div>
                                ) : isPdfUrl(newDocUrl) ? (
                                  <iframe
                                    src={fullUrl}
                                    title="Nouveau document"
                                    className="w-full h-48"
                                  />
                                ) : (
                                  <div className="h-48 flex items-center justify-center text-gray-400">
                                    <FileText className="w-12 h-12" />
                                  </div>
                                )}
                                <div className="p-2 border-t border-green-200 flex justify-center">
                                  <a
                                    href={fullUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs text-green-600 hover:text-green-700 flex items-center gap-1"
                                  >
                                    <ExternalLink className="w-3 h-3" />
                                    Ouvrir en grand
                                  </a>
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      </div>

                      {/* Document reason/comment */}
                      {(request.reason || extractDocumentReason(request.requestedValue)) && (
                        <div className="bg-blue-50 rounded-lg p-4">
                          <span className="text-sm font-medium text-gray-500">Commentaire</span>
                          <p className="mt-2 text-gray-700">
                            {request.reason || extractDocumentReason(request.requestedValue)}
                          </p>
                        </div>
                      )}
                    </div>
                  ) : (
                    /* Regular field change - Show text values */
                    <>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-gray-50 rounded-lg p-4">
                          <span className="text-sm font-medium text-gray-500">Valeur actuelle</span>
                          <p className="mt-2 text-gray-700 font-mono bg-white px-3 py-2 rounded border border-gray-200">
                            {request.currentValue || <span className="text-gray-400 italic">(non renseigné)</span>}
                          </p>
                        </div>
                        <div className="bg-green-50 rounded-lg p-4">
                          <span className="text-sm font-medium text-gray-500">Nouvelle valeur demandée</span>
                          <div className="mt-2 flex items-center gap-2">
                            <ArrowRight className="w-4 h-4 text-green-600 flex-shrink-0" />
                            <p className="text-green-800 font-medium font-mono bg-white px-3 py-2 rounded border border-green-200 flex-1">
                              {request.requestedValue}
                            </p>
                          </div>
                        </div>
                      </div>

                      {request.reason && (
                        <div className="bg-blue-50 rounded-lg p-4">
                          <span className="text-sm font-medium text-gray-500">Raison de la demande</span>
                          <p className="mt-2 text-gray-700">{request.reason}</p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Document Verification Warning */}
              {showDocWarning && !loadingDocs && (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-yellow-800">Vérification recommandée</p>
                    <p className="text-sm text-yellow-700 mt-1">
                      Ce champ nécessite généralement une vérification documentaire (CIN, Passeport).
                      L'utilisateur n'a pas encore téléchargé de documents.
                    </p>
                  </div>
                </div>
              )}

              {/* Documents Section */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-5 py-3 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <FileText className="w-5 h-5 text-gray-600" />
                      <h3 className="font-medium text-gray-900">Documents de l'utilisateur</h3>
                      <span className="px-2 py-0.5 bg-gray-200 text-gray-600 rounded-full text-xs">
                        {documents.length}
                      </span>
                    </div>
                    {documents.length > 0 && (
                      <button
                        onClick={() => setShowDocumentViewer(!showDocumentViewer)}
                        className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                      >
                        {showDocumentViewer ? 'Masquer' : 'Voir les documents'}
                      </button>
                    )}
                  </div>
                </div>

                {loadingDocs ? (
                  <div className="p-8 flex items-center justify-center">
                    <Loader2 className="w-6 h-6 text-primary-600 animate-spin" />
                    <span className="ml-2 text-gray-500">Chargement des documents...</span>
                  </div>
                ) : docsError ? (
                  <div className="p-4 text-center text-red-600">
                    <AlertCircle className="w-6 h-6 mx-auto mb-2" />
                    <p className="text-sm">{docsError}</p>
                  </div>
                ) : documents.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    <FileText className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                    <p className="text-sm">Aucun document disponible</p>
                  </div>
                ) : (
                  <>
                    {/* Document List */}
                    <div className="p-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {documents.map((doc, index) => (
                        <button
                          key={doc.id}
                          onClick={() => {
                            setSelectedDocIndex(index);
                            setShowDocumentViewer(true);
                          }}
                          className={`p-3 border rounded-lg text-left transition-all ${
                            showDocumentViewer && selectedDocIndex === index
                              ? 'border-primary-500 bg-primary-50'
                              : 'border-gray-200 hover:border-primary-300 hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            {isImageDocument(doc) ? (
                              <ImageIcon className="w-4 h-4 text-blue-600" />
                            ) : (
                              <FileText className="w-4 h-4 text-gray-600" />
                            )}
                            <span className="text-xs font-medium text-gray-900 truncate">
                              {DOCUMENT_TYPE_LABELS[doc.documentType] || doc.documentType}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 truncate">{doc.fileName}</p>
                          {doc.isVerified && (
                            <span className="inline-flex items-center gap-1 mt-1 text-xs text-green-600">
                              <CheckCircle className="w-3 h-3" />
                              Vérifié
                            </span>
                          )}
                        </button>
                      ))}
                    </div>

                    {/* Document Viewer */}
                    {showDocumentViewer && documents[selectedDocIndex] && (
                      <div className="border-t border-gray-200 bg-gray-900 p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={handlePrevDocument}
                              className="p-1.5 text-white bg-white/20 rounded-lg hover:bg-white/30 transition-colors"
                              disabled={documents.length <= 1}
                            >
                              <ChevronLeft className="w-4 h-4" />
                            </button>
                            <span className="text-white text-sm">
                              {selectedDocIndex + 1} / {documents.length}
                            </span>
                            <button
                              onClick={handleNextDocument}
                              className="p-1.5 text-white bg-white/20 rounded-lg hover:bg-white/30 transition-colors"
                              disabled={documents.length <= 1}
                            >
                              <ChevronRight className="w-4 h-4" />
                            </button>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-white/70 text-sm">
                              {DOCUMENT_TYPE_LABELS[documents[selectedDocIndex].documentType] ||
                               documents[selectedDocIndex].documentType}
                            </span>
                            <a
                              href={getDocumentUrl(documents[selectedDocIndex])}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-1.5 text-white bg-white/20 rounded-lg hover:bg-white/30 transition-colors"
                              title="Ouvrir dans un nouvel onglet"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          </div>
                        </div>

                        <div className="bg-white rounded-lg overflow-hidden flex items-center justify-center min-h-[300px] max-h-[400px]">
                          {isImageDocument(documents[selectedDocIndex]) ? (
                            <img
                              src={getDocumentUrl(documents[selectedDocIndex])}
                              alt={documents[selectedDocIndex].fileName}
                              className="max-w-full max-h-[400px] object-contain"
                              onError={(e) => {
                                (e.target as HTMLImageElement).src = '/placeholder-document.png';
                              }}
                            />
                          ) : (
                            <iframe
                              src={getDocumentUrl(documents[selectedDocIndex])}
                              title={documents[selectedDocIndex].fileName}
                              className="w-full h-[400px]"
                            />
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Reject Form */}
              {showRejectForm && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-3">
                  <div className="flex items-center gap-2 text-red-700">
                    <XCircle className="w-5 h-5" />
                    <span className="font-medium">Refuser la demande</span>
                  </div>
                  <textarea
                    value={rejectComment}
                    onChange={(e) => setRejectComment(e.target.value)}
                    placeholder="Raison du refus (obligatoire)..."
                    rows={3}
                    className="w-full px-3 py-2 border border-red-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
                    autoFocus
                  />
                  <div className="flex items-center gap-3">
                    <button
                      onClick={handleReject}
                      disabled={processing || !rejectComment.trim()}
                      className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                    >
                      {processing ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <XCircle className="w-4 h-4" />
                      )}
                      Confirmer le refus
                    </button>
                    <button
                      onClick={() => {
                        setShowRejectForm(false);
                        setRejectComment('');
                      }}
                      disabled={processing}
                      className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors text-sm"
                    >
                      Annuler
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
            <button
              onClick={onClose}
              disabled={processing}
              className="px-4 py-2 text-gray-700 hover:text-gray-900 transition-colors"
            >
              Fermer
            </button>
            {!showRejectForm && (
              <>
                <button
                  onClick={() => setShowRejectForm(true)}
                  disabled={processing}
                  className="flex items-center gap-2 px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                >
                  <XCircle className="w-4 h-4" />
                  Refuser
                </button>
                <button
                  onClick={handleApprove}
                  disabled={processing}
                  className="flex items-center gap-2 px-5 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                >
                  {processing ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <CheckCircle className="w-4 h-4" />
                  )}
                  Approuver la modification
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Fullscreen Document Preview Modal */}
      {showFullscreenPreview && request && isDocumentChangeRequest(request.fieldName, request.requestedValue) && (
        <div className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center">
          <button
            onClick={() => {
              setShowFullscreenPreview(false);
              setDocPreviewZoom(100);
              setDocPreviewRotation(0);
            }}
            className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-lg text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>

          {/* Controls */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-black/50 rounded-lg p-2">
            <button
              onClick={() => setDocPreviewZoom(z => Math.max(25, z - 25))}
              className="p-2 text-white hover:bg-white/20 rounded-lg transition-colors"
              title="Réduire"
            >
              <ZoomOut className="w-5 h-5" />
            </button>
            <span className="text-white text-sm min-w-[60px] text-center">{docPreviewZoom}%</span>
            <button
              onClick={() => setDocPreviewZoom(z => Math.min(300, z + 25))}
              className="p-2 text-white hover:bg-white/20 rounded-lg transition-colors"
              title="Agrandir"
            >
              <ZoomIn className="w-5 h-5" />
            </button>
            <div className="w-px h-6 bg-white/30 mx-1" />
            <button
              onClick={() => setDocPreviewRotation(r => (r + 90) % 360)}
              className="p-2 text-white hover:bg-white/20 rounded-lg transition-colors"
              title="Pivoter"
            >
              <RotateCw className="w-5 h-5" />
            </button>
            <div className="w-px h-6 bg-white/30 mx-1" />
            <a
              href={getFullDocumentUrl(extractDocumentUrl(request.requestedValue))}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 text-white hover:bg-white/20 rounded-lg transition-colors"
              title="Ouvrir dans un nouvel onglet"
            >
              <ExternalLink className="w-5 h-5" />
            </a>
          </div>

          {/* Image */}
          <div className="max-w-[90vw] max-h-[85vh] overflow-auto">
            {(() => {
              const newDocUrl = extractDocumentUrl(request.requestedValue);
              const fullUrl = getFullDocumentUrl(newDocUrl);
              if (isImageUrl(newDocUrl)) {
                return (
                  <img
                    src={fullUrl}
                    alt="Nouveau document"
                    className="transition-transform duration-200"
                    style={{
                      transform: `scale(${docPreviewZoom / 100}) rotate(${docPreviewRotation}deg)`,
                    }}
                  />
                );
              } else if (isPdfUrl(newDocUrl)) {
                return (
                  <iframe
                    src={fullUrl}
                    title="Nouveau document"
                    className="w-[80vw] h-[80vh]"
                  />
                );
              } else {
                return (
                  <div className="text-white text-center">
                    <FileText className="w-24 h-24 mx-auto mb-4" />
                    <p>Prévisualisation non disponible</p>
                    <a
                      href={fullUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-400 hover:text-primary-300 mt-2 inline-block"
                    >
                      Télécharger le document
                    </a>
                  </div>
                );
              }
            })()}
          </div>
        </div>
      )}
    </div>
  );
};

export default ChangeRequestReviewModal;
